import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Layout, Card } from 'antd'
import { useRouteMatch } from 'react-router-dom'
import { CopyrightCircleOutlined, AppleFilled, AndroidFilled } from '@ant-design/icons'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)

import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { logout } from '../../services/auth/logout-handler'
import { MenuContext } from '../../context/menuContext'
import { notificationStore } from '../../context/notificationsContext/store'

import Sidebar from '../Sidebar/index'
// import Topbar from '../Topbar/index'
import MobileHeader from '../MobileHeader/index'
import MainAppCompanions from './MainAppCompanions'
import VeeAssistant from '../../components/vee-assistant'
import App from '../../routes/index'

import CircularProgress from '../../components/circular-progress'
import MobileBanner from '../../components/mobile-banner'
import TrialBanner from '../../components/trial-banner'
import FreePlanBanner from '../../components/freemium-plan-banner'
import PastDueBanner from '../../components/past-due-banner'
import PlanBoostBanner from '../../components/plan-boost-banner'
import { usePlanBoostBanner } from '../../components/plan-boost-banner/use-plan-boost-banner'
import { useManualQuery } from 'graphql-hooks'
import { getNumberOfActiveUsers } from '../../graphql/custom-queries'
import { useIsVeeEnabled } from '../../hooks/use-is-vee-enabled'

import IntlMessages from '../../util/IntlMessages'
import { ANDROID_MOBILE_APP_URL, IOS_MOBILE_APP_URL } from '../../constants'

import { IUserInfo } from '../../types/users'

const { Content, Footer } = Layout

interface IMainApp {
  user: IUserInfo
}

const MainApp = ({ user }: IMainApp): React.ReactElement => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const dispatch = useAppDispatch()
  const history = useHistory()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  // Owned here because the open panel reserves layout width via the app-layout className
  const [isVeePanelOpen, setIsVeePanelOpen] = useState(false)
  const isVeeEnabled = useIsVeeEnabled()

  useEffect(() => {
    if (isMobile || !isVeeEnabled) {
      setIsVeePanelOpen(false)
    }
  }, [isMobile, isVeeEnabled])
  const { miniMenu } = useContext(MenuContext)
  const { eventsNotifications } = useContext(notificationStore)

  // Check if trial banner should be shown
  const shouldShowTrialBanner = authCompany?.subscriptionStatus === 'trialing'
  const shouldShowPastDueBanner = authCompany?.subscriptionStatus === 'past_due'
  const planBoostBanner = usePlanBoostBanner(user.role)
  // Fixed banners overlap each other, so the boost banner gives way to the billing ones
  const shouldShowPlanBoostBanner = !shouldShowTrialBanner && !shouldShowPastDueBanner && planBoostBanner !== null
  // These banners share the fixed top-banner layout offset
  const shouldOffsetForBanner = shouldShowTrialBanner || shouldShowPastDueBanner || shouldShowPlanBoostBanner

  // For layout offset: also account for Free Plan banner presence (Freemium, admin, and < 2 users)
  const [fetchActiveUsers] = useManualQuery<{ getCompany: { numberOfActiveUsers: number } }>(getNumberOfActiveUsers)
  const [shouldShowFreePlanBanner, setShouldShowFreePlanBanner] = useState(false)

  useEffect(() => {
    // Only admins should see the Free Plan banner
    const isAdmin = user.role === 'Admin'
    if (!isAdmin) {
      setShouldShowFreePlanBanner(false)
      return
    }

    const isFreemium = (authCompany?.billing?.subscriptionPlan || authCompany?.plan) === 'Freemium'
    const isTrial = authCompany?.subscriptionStatus === 'trialing'

    // Only show for Freemium, not for trials
    if (!isFreemium || isTrial) {
      setShouldShowFreePlanBanner(false)
      return
    }

    // Fetch active users and set banner visibility directly
    let isMounted = true
    const load = async () => {
      try {
        const res = await fetchActiveUsers()
        if (!isMounted) return
        const count = res?.data?.getCompany?.numberOfActiveUsers ?? 0
        setShouldShowFreePlanBanner(count < 2)
      } catch {
        if (!isMounted) return
        setShouldShowFreePlanBanner(false)
      }
    }
    void load()

    return () => {
      isMounted = false
    }
  }, [fetchActiveUsers, authCompany?.plan, authCompany?.subscriptionStatus, user.role, eventsNotifications])

  useEffect(() => {
    document.body.classList.remove('flow-b-theme')
  }, [authCompany])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const match = useRouteMatch()

  const onLogout = useCallback(() => {
    logout({
      history,
      reduxDispatch: dispatch,
      userId: user.id,
    })
  }, [history, dispatch, user.id])

  return (
    <>
      {!isMobile && <MobileBanner />}
      {isMobile ? (
        // Mobile Layout
        <>
          <MobileHeader onLogout={onLogout} />
          <TrialBanner sidebarCollapsed={false} />
          <FreePlanBanner sidebarCollapsed={false} visible={shouldShowFreePlanBanner} />
          <PastDueBanner sidebarCollapsed={false} />
          {shouldShowPlanBoostBanner && <PlanBoostBanner {...planBoostBanner} sidebarCollapsed={false} />}
          <Layout className={`app-layout mobile-layout ${shouldOffsetForBanner ? 'with-trial-banner' : ''}`}>
            <Content className={`mobile-content ${shouldOffsetForBanner ? 'with-trial-banner' : ''}`}>
              <Card
                bordered={false}
                style={{
                  margin: '16px',
                  borderRadius: '12px',
                  minHeight: 'calc(100vh - 64px - 60px - 32px)', // viewport - header - footer - margins
                }}
              >
                {!user ? <CircularProgress /> : <App match={match} />}
              </Card>
              <Footer className="mobile-footer">
                <IntlMessages id="app.copyright" /> <CopyrightCircleOutlined /> {dayjs().format('YYYY')}• <IntlMessages id="app.mobileAppInstall" />{' '}
                <Button icon={<AppleFilled />} size="small" type="primary" href={IOS_MOBILE_APP_URL} target="_blank" rel="noopener noreferrer" className="app-download-button">
                  <IntlMessages id="app.mobileAppDownloadIos" />
                </Button>{' '}
                <Button
                  icon={<AndroidFilled />}
                  size="small"
                  type="primary"
                  href={ANDROID_MOBILE_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-download-button"
                >
                  <IntlMessages id="app.mobileAppDownloadAndroid" />
                </Button>
              </Footer>
            </Content>
          </Layout>
        </>
      ) : (
        // Desktop Layout
        <>
          <TrialBanner sidebarCollapsed={miniMenu} />
          <FreePlanBanner sidebarCollapsed={miniMenu} visible={shouldShowFreePlanBanner} />
          <PastDueBanner sidebarCollapsed={miniMenu} />
          {shouldShowPlanBoostBanner && <PlanBoostBanner {...planBoostBanner} sidebarCollapsed={miniMenu} showReviewAction />}
          <Layout className={['app-layout', isVeeEnabled ? 'vee-docked' : '', shouldOffsetForBanner ? 'with-trial-banner' : '', isVeePanelOpen ? 'vee-panel-open' : ''].join(' ')}>
            <Sidebar onLogout={onLogout} />
            <Layout className="content-layout">
              {/* <TopBar /> */}
              <Content>
                {!user ? <CircularProgress /> : <App match={match} />}
                <Footer>
                  <IntlMessages id="app.copyright" /> <CopyrightCircleOutlined /> {dayjs().format('YYYY')}• <IntlMessages id="app.mobileAppInstall" />{' '}
                  <Button icon={<AppleFilled />} size="small" type="primary" href={IOS_MOBILE_APP_URL} target="_blank" rel="noopener noreferrer" className="app-download-button">
                    <IntlMessages id="app.mobileAppDownloadIos" />
                  </Button>{' '}
                  <Button
                    icon={<AndroidFilled />}
                    size="small"
                    type="primary"
                    href={ANDROID_MOBILE_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-download-button"
                  >
                    <IntlMessages id="app.mobileAppDownloadAndroid" />
                  </Button>
                </Footer>
              </Content>
            </Layout>
            <MainAppCompanions />
            {isVeeEnabled && <VeeAssistant isOpen={isVeePanelOpen} onOpenChange={setIsVeePanelOpen} />}
          </Layout>
        </>
      )}
    </>
  )
}
export default MainApp
